import { action, makeObservable, observable } from 'mobx'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import i18n from 'i18next'

import { getTitle } from '../../../../utils/QuestionHelper'

class CertificationStore {
    gatewayEndpoint = '/intranet/certification'

    @observable isLoading = true

    productMap = {}

    lenses = {
        industry: R.lensProp('industry'),
        productType: R.lensProp('productType'),
        surveyType: R.lensProp('surveyType'),
        respondentType: R.lensProp('respondentType'),
    }

    @observable formData = {
        questions: [],
        flags: {
            email: false,
            campaign: false,
            onSite: false,
            fieldwork: false,
            emailSampleSize: false,
            otherSampleSize: false,
            selfSelectQuestions: false,
        },
        selfSelectQuestions: [],
    }

    @observable questions = []

    @observable searchData = []

    flagFields = [
        {
            name: 'email',
        },
        {
            name: 'campaign',
        },
        {
            name: 'onSite',
        },
        {
            name: 'fieldwork',
            child: {
                name: 'fieldworkDays',
                label: 'Days',
                className: 'fieldWorkDays',
            },
            fieldResets: (data) => {
                data.fieldworkDays = undefined
            },
        },
        {
            name: 'emailSampleSize',
        },
        {
            name: 'otherSampleSize',
            child: {
                isText: true,
                name: 'otherSampleSize',
                placeholder: 'Number of visitors / exhibitors onsite',
            },
            fieldResets: (data) => {
                data.otherSampleSize = undefined
            },
        },
        {
            name: 'selfSelectQuestions',
            fieldResets: (data) => {
                data.selfSelectQuestions = []
            },
        },
    ]

    getData = async (id) => {
        const { appStore, gatewayEndpoint } = this
        let data = {}

        if (id === 0) return data

        try {
            data = await appStore.http.get(`${gatewayEndpoint}/${id}`)
        } catch (e) {
            console.error(e)
            appStore.handleError({ body: `Error loading certification ${id}`, level: 'error' })
        }

        return data
    }

    setBreadcrumbs = (path = []) => {
        const { appStore } = this
        appStore.breadcrumps = [
            {
                name: 'intranet',
                url: '/intranet',
                title: 'Intranet',
            },
            {
                name: 'certification',
                url: '/intranet/certification',
                title: 'Certification',
            },
            ...path,
        ]
    }

    initFormData = (data) => {
        const formData = {
            id: R.pathOr(0, ['id'], data),
            industry: R.pathOr(undefined, ['industry'], data),
            productType: R.pathOr(undefined, ['productType'], data),
            surveyType: R.pathOr(undefined, ['surveyType'], data),
            respondentType: R.pathOr(undefined, ['respondentType'], data),
            questions: R.pathOr([], ['rules', 'questions'], data).map((questionId) => ({
                id: uuidv4(),
                questionId,
            })),
            minQuestionsAnswered: R.pathOr(0, ['rules', 'minQuestionsAnswered'], data),
            flags: {
                email: R.pathOr(false, ['rules', 'flags', 'email'], data),
                campaign: R.pathOr(false, ['rules', 'flags', 'campaign'], data),
                onSite: R.pathOr(false, ['rules', 'flags', 'onSite'], data),
                fieldwork: R.pathOr(false, ['rules', 'flags', 'fieldwork'], data),
                emailSampleSize: R.pathOr(false, ['rules', 'flags', 'emailSampleSize'], data),
                otherSampleSize: R.pathOr(false, ['rules', 'flags', 'otherSampleSize'], data),
                selfSelectQuestions: R.pathOr(false, ['rules', 'flags', 'selfSelectQuestions'], data),
            },
            otherSampleSize: R.pathOr(undefined, ['rules', 'otherSampleSize'], data),
            fieldworkDays: R.pathOr(undefined, ['rules', 'fieldworkDays'], data),
            selfSelectQuestions: R.pathOr([], ['rules', 'selfSelectQuestions'], data).map(({ value, flags }) => ({
                id: uuidv4(),
                value,
                flags: new Set(flags),
            })),
        }

        this.formData = formData
    }

    constructor(store) {
        makeObservable(this)
        this.appStore = store
    }

    initSearch = async () => {
        this.isLoading = true
        this.searchData = await this.search()
        this.setBreadcrumbs()
        this.isLoading = false
    }

    search = async () => {
        const { appStore, gatewayEndpoint } = this

        let data = []

        try {
            data = await appStore.http.get(gatewayEndpoint)
        } catch (e) {
            console.error(e)
            appStore.handleError({ body: i18n.t('certification.search.errorLoading'), level: 'error' })
        }

        return data
    }

    delete = async (id) => {
        const { appStore, gatewayEndpoint } = this
        const { searchData } = this

        try {
            await appStore.http.delete(`${gatewayEndpoint}/${id}`)
            const dataCopy = searchData.slice().filter((row) => row.id !== id)
            appStore.addNotification({ body: i18n.t('certification.deleted'), level: 'success' })
            this.searchData = dataCopy
        } catch (e) {
            console.error(e)
            appStore.handleError({ body: i18n.t('certification.errorDeleting'), level: 'error' })
        }
    }

    init = async () => {
        this.setFlagFieldLabels()
    }

    setFlagFieldLabels = () => {
        this.flagFields = this.flagFields.map((field) => {
            field.label = i18n.t(`certification.flag.${field.name}`)

            return field
        })
    }

    @action
    initEdit = async (certificateId = 0) => {
        this.isLoading = true

        const [data] = await Promise.all([
            this.getData(certificateId),
            this.getProductMap(),
        ])

        const title = certificateId === 0
            ? 'Create'
            : `Edit (${certificateId})`

        this.setBreadcrumbs([{
            name: 'editCreate',
            url: `/intranet/certification/${certificateId}`,
            title,
        }])
        this.initFormData(data)

        if (certificateId !== 0) {
            await this.getQList()
        }

        this.isLoading = false
    }

    save = async () => {
        const { formData, appStore, gatewayEndpoint } = this
        const questions = formData.questions
            .filter(({ questionId }) => !R.isNil(questionId))
            .map(({ questionId }) => questionId)
        const json = {
            id: formData.id,
            industry: formData.industry,
            productType: formData.productType,
            surveyType: formData.surveyType,
            respondentType: formData.respondentType,
            rules: {
                questions,
                minQuestionsAnswered: R.or(formData.minQuestionsAnswered, 0),
                flags: formData.flags,
                otherSampleSize: formData.otherSampleSize,
                fieldworkDays: formData.fieldworkDays,
                selfSelectQuestions: formData.selfSelectQuestions.map(({ value, flags }) => ({
                    value,
                    flags: Array.from(flags),
                })),
            },
        }

        try {
            if (json.id === 0) {
                await appStore.http.post(`${gatewayEndpoint}`, JSON.stringify(json))
            } else {
                await appStore.http.put(`${gatewayEndpoint}/${json.id}`, JSON.stringify(json))
            }
            appStore.addNotification({ body: i18n.t('certification.saved'), level: 'success' })
            location.href = '/intranet/certification'
        } catch (e) {
            console.error(e)
            appStore.handleError({ body: i18n.t('certification.errorSaving'), level: 'error' })
        }
    }

    saveIsDisabled = () => (
        R.isNil(R.view(this.lenses.surveyType, this.formData))
    )

    getForm = () => {
        const { formData } = this
        const { industry, productType, respondentType } = formData

        return [
            {
                name: 'industry',
                label: i18n.t('industry'),
                type: 'select',
                options: this.getIndustries,
                path: ['industry'],
                fieldResets: (data) => {
                    Reflect.deleteProperty(data, 'productType')
                    Reflect.deleteProperty(data, 'respondentType')
                    Reflect.deleteProperty(data, 'surveyType')
                    data.questions = []
                    this.questions = []
                },
                show: () => true,
            },
            {
                name: 'productType',
                label: i18n.t('product_type'),
                type: 'select',
                options: this.getProductTypes,
                path: ['productType'],
                key: `${industry}_productType`,
                fieldResets: (data) => {
                    Reflect.deleteProperty(data, 'respondentType')
                    Reflect.deleteProperty(data, 'surveyType')
                    data.questions = []
                    this.questions = []
                },
                show: () => (
                    !R.isNil(R.view(this.lenses.industry, this.formData))
                ),
            },
            {
                name: 'respondentType',
                label: i18n.t('respondent_type'),
                key: `${industry}_${productType}_respondentType`,
                type: 'select',
                options: this.getRespondentTypes,
                path: ['respondentType'],
                fieldResets: (data) => {
                    Reflect.deleteProperty(data, 'surveyType')
                    data.questions = []
                    this.questions = []
                },
                show: () => (
                    !R.isNil(R.view(this.lenses.productType, this.formData))
                ),
            },
            {
                name: 'surveyType',
                label: i18n.t('survey_type'),
                type: 'select',
                options: this.getSurveyTypes,
                key: `${industry}_${productType}_${respondentType}_surveyType`,
                path: ['surveyType'],
                fieldResets: (data) => {
                    data.questions = []
                    this.questions = []
                },
                show: () => (
                    !R.isNil(R.view(this.lenses.respondentType, this.formData))
                ),
                promise: this.getQList,
            },
            {
                name: 'questions',
                type: 'questions',
                path: ['questions'],
                show: () => !R.isNil(R.view(this.lenses.surveyType, this.formData)),
            },
            {
                name: 'flags',
                type: 'flags',
                path: ['flags'],
                show: () => true,
            },
            {
                name: 'selfSelect',
                type: 'selfSelect',
                path: [],
                show: () => R.pathOr(false, ['flags', 'selfSelectQuestions'], this.formData),
            },
        ].filter(({ show }) => show())
    }

    getQuestionTitle = ({ locales, initialLocale }) => (
        R.pathOr('not found', ['text'], getTitle({
            content: locales,
            locale: 'en_GB',
            defaultLocale: 'en_GB',
            initialLocale,
        }))
    )

    getQList = async () => {
        const { appStore, formData } = this
        const {
            industry,
            productType,
            respondentType,
            surveyType,
        } = formData

        let qlisturl = '/qlist/getQlists2?'
        qlisturl += '$inlinecount=allpages'
        qlisturl += '&$format=json'
        qlisturl += '&recordId=1'
        qlisturl += '&oid=3'
        qlisturl += `&productType=${productType}`
        qlisturl += `&respondentType=${respondentType}`
        qlisturl += `&surveyType=${surveyType}`
        qlisturl += '&cols=surveyBuilderReact'
        qlisturl += '&langcode=en_GB'
        qlisturl += `&industry=${industry}`

        let questions = []

        try {
            questions = await appStore.http.get(qlisturl)
        } catch (e) {
            console.error(e)
        }

        this.questions = questions.map(({ questionId, locales, initialLocale }) => ({
            value: questionId,
            label: this.getQuestionTitle({ locales, initialLocale }),
        }))
    }

    @action
    onChange = async ({ value }, promise, path, fieldResets) => {
        if (typeof fieldResets === 'function') {
            fieldResets(this.formData)
        }
        this.formData = R.set(R.lensPath(path), value, this.formData)

        if (typeof promise === 'function') {
            await promise()
        }
    }

    getIndustries = () => (
        Object.entries(this.productMap).map(([value, { name: label }]) => ({
            value,
            label,
        }))
    )

    getProductTypes = () => (
        Object.entries(
            R.pathOr({}, [this.formData.industry, 'productTypes'], this.productMap),
        ).map(([value, { name: label }]) => ({
            value,
            label,
        }))
    )

    getRespondentTypes = () => (
        Object.entries(
            R.pathOr({}, [this.formData.industry, 'productTypes', this.formData.productType, 'respondentTypes'], this.productMap),
        ).map(([value, { name: label }]) => ({
            value,
            label,
        }))
    )

    getSurveyTypes = () => (
        Object.entries(
            R.pathOr({}, [this.formData.industry, 'productTypes', this.formData.productType, 'respondentTypes', this.formData.respondentType, 'surveyTypes'], this.productMap),
        ).map(([value, { name: label }]) => ({
            value,
            label,
        }))
    )

    getProductMap = async () => {
        const { appStore } = this
        const productMaps = await appStore.http.get('/productmap')

        productMaps.forEach((map) => {
            const {
                industrycode: industryCode,
                producttypecode: productTypeCode,
                respondenttypecode: respondentTypeCode,
                surveytypecode: surveyTypeCode,
                industryname: industry,
                producttypename: productType,
                respondenttypename: respondentType,
                surveytypename: surveyType,
            } = map

            const productTypePath = R.lensPath([industryCode, 'productTypes', productTypeCode])
            const respondentTypePath = R.lensPath([industryCode, 'productTypes', productTypeCode, 'respondentTypes', respondentTypeCode])
            const surveyTypePath = R.lensPath([industryCode, 'productTypes', productTypeCode, 'respondentTypes', respondentTypeCode, 'surveyTypes', surveyTypeCode])

            if (!R.hasPath([industryCode], this.productMap)) {
                this.productMap[industryCode] = {
                    name: industry,
                    productTypes: {},
                }
            }

            if (R.isNil(R.view(productTypePath, this.productMap))) {
                this.productMap = R.set(productTypePath, {
                    name: productType,
                    respondentTypes: {},
                }, this.productMap)
            }

            if (R.isNil(R.view(respondentTypePath, this.productMap))) {
                this.productMap = R.set(respondentTypePath, {
                    name: respondentType,
                    surveyTypes: {},
                }, this.productMap)
            }

            if (R.isNil(R.view(surveyTypePath, this.productMap))) {
                this.productMap = R.set(surveyTypePath, {
                    name: surveyType,
                }, this.productMap)
            }
        })
    }
}

export default CertificationStore
